import { createContext, useState, useContext, ReactNode } from 'react';
import { Modal, ModalProps } from 'vopay';

type DialogCtx = {
  open: <ReturnType>(
    Component: React.ComponentType<OpenDialogProps<ReturnType>>,
    options?: Options,
  ) => Promise<ReturnType | null>;
};

export type OpenDialogProps<ReturnType> = {
  onConfirm: (value: ReturnType) => void;
  onCancel: () => void;
};

type Options = {
  bgClick?: boolean;
  onBgClick?: () => void;
};

export const DialogContext = createContext<DialogCtx>({
  open: () => Promise.resolve(null),
});

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  const [dialogProps, setDialogProps] = useState<Partial<ModalProps>>({});
  const [dialog, setDialog] = useState<ReactNode>(null);
  const [showingDialog, setShowingDialog] = useState(false);

  function open<ReturnType>(Component: React.ComponentType<OpenDialogProps<ReturnType>>, options: Options = {}) {
    return new Promise<ReturnType | null>((resolve) => {
      setDialogProps({
        ...dialogProps,
        enableBgClick: options.bgClick === undefined ? false : options.bgClick,
        onBgClick: options.onBgClick,
      });

      const confirm = (value: ReturnType) => {
        setShowingDialog(false);
        resolve(value);
        setDialog(null);
      };

      const cancel = () => {
        setShowingDialog(false);
        resolve(null);
        setDialog(null);
      };

      setDialog(<Component onConfirm={confirm} onCancel={cancel} />);
      setShowingDialog(true);
    });
  }

  return (
    <DialogContext.Provider value={{ open }}>
      {children}

      <Modal isVisible={showingDialog} {...dialogProps}>
        {dialog}
      </Modal>
    </DialogContext.Provider>
  );
};

export const useDialog = () => useContext(DialogContext);
